import PropTypes from 'prop-types';
import React from 'react';
import { Box, Card, Grid2 as Grid, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { get } from 'lodash';
import StatusBadge from '../StatusBadge';
import CardHeader from '../CardHeader';
import CardActionArea from '../PostCard/partials/CardActionArea';
import CardContent from '../PostCard/partials/CardContent';
import CardMedia from './partials/CardMedia';
import { PostStatuses } from '../../../Utils/Types';
import PostIndicators from '../../Posts/Shared/PostIndicators';
import ParentLink from './partials/ParentLink';
import ResponsiveDivider from './partials/ResponsiveDivider';
import InteractionsBox from '../../Posts/Shared/InteractionsBox';
import IdeaCostMeta from './partials/IdeaCostMeta';
import AnalyticsService from '../../../Services/AnalyticsService';
import { Images } from '../../../Themes';
import { postPath } from '../../../Utils/routes';
import { cloudinaryImageUrl, CloudinaryConfig } from '../../../Services/Cloudinary';
import { isVoteStepOngoing } from '../../../Utils/Steps';
import { PostType } from '../../../Utils/Types/Posts';
import { SteppablePostType } from '../../../Utils/Types/Steps';

function PostListItem({
  hideButtons = false,
  newTab = false,
  parent,
  parentLink,
  post,
  space,
  stepTab,
  trackingProps,
}: any) {
  const {
    current_status: { keyword },
    included: { author },
  } = post;
  const isVoteStep =
    parent &&
    isVoteStepOngoing(
      parent?.included?.steps || [parent?.current_step],
      parent?.type as SteppablePostType,
    );
  const voteTab =
    parent && stepTab && isVoteStepOngoing([stepTab], parent?.type as SteppablePostType);
  const statusUnlessTodo = keyword !== PostStatuses.TODO;
  const showButtons = isVoteStep && voteTab && !hideButtons;
  const showPicture = get(parent, 'display_options.children_list_pictures');
  const postImage =
    cloudinaryImageUrl(
      post.picture?.s3_key,
      'w_500,h_200,q_auto:low,c_fill,g_auto',
      post.picture?.title,
    ) || Images.posts.banner[post.type as PostType];

  const onClick = () => {
    if (trackingProps?.category && trackingProps?.name) {
      AnalyticsService.track(trackingProps.category, trackingProps.name, '', {
        'Post ID': post.id,
        'Post Type': post.type,
        'Post Pinned': post.is_pinned,
      });
    }
  };

  return (
    <Card elevation={0} sx={{ width: '100%', borderRadius: 0, textAlign: 'start' }}>
      {parentLink && parent && <ParentLink slug={parent.slug} space={space} title={parent.title} />}

      <CardActionArea
        to={postPath(space.slug, post.slug)}
        component={Link}
        onClick={onClick}
        underline="none"
        target={newTab ? '_blank' : '_self'}
      >
        {showPicture && <CardMedia image={postImage} />}

        <CardContent sx={{ pb: 0 }} showPicture>
          <Box sx={{ mb: 2 }}>
            <CardHeader
              profilePictureUrl={cloudinaryImageUrl(
                author.picture?.s3_key,
                CloudinaryConfig.AVATAR_30,
                author.picture?.title,
              )}
              initials={author.initials}
              fullName={author.full_name}
              alt={author.full_name}
              date={post.published_at}
              avatarSize={30}
              placeholderType="user"
            />
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 'grow' }}>
              <Typography variant="h6" component="h2" className="line-clamp">
                {post.title}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  mt: 3,
                }}
              >
                {post.current_status && statusUnlessTodo && (
                  <Box
                    sx={{
                      mr: 2,
                      mb: 2,
                    }}
                  >
                    <StatusBadge status={post.current_status} size="small" />
                  </Box>
                )}

                {statusUnlessTodo && <ResponsiveDivider />}

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    mb: 2,
                  }}
                >
                  <PostIndicators
                    stepTab={stepTab}
                    postContributionsStats={post.post_contributions_stats}
                  />

                  {post.budget && (
                    <Box sx={{ ml: 2.5 }}>
                      <IdeaCostMeta post={post} space={space} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {showButtons && (
              <Grid size={{ xs: 12, md: 'auto' }}>
                <Box sx={{ mb: { xs: 2, md: 0 } }}>
                  <InteractionsBox
                    post={post}
                    space={space}
                    direction={parent.voting_options?.type === 'double' ? 'row' : 'column'}
                    ctaVariant="outlined"
                    onlyPrimaryActions
                    showLabel={parent.voting_options?.type !== 'double'}
                    isResponsive
                    size="small"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

PostListItem.propTypes = {
  trackingProps: PropTypes.shape({
    category: PropTypes.string,
    name: PropTypes.string,
  }),
  currentStep: PropTypes.shape({
    step_type: PropTypes.string,
  }),
  space: PropTypes.shape({
    id: PropTypes.number,
  }),
  parent: PropTypes.shape({
    id: PropTypes.number,
  }),
  parentLink: PropTypes.bool,
  post: PropTypes.shape({
    id: PropTypes.number,
  }),
  stepTab: PropTypes.shape({
    step_type: PropTypes.string,
  }),
  hideButtons: PropTypes.bool,
  newTab: PropTypes.bool,
};

export default PostListItem;
